import { Box, ButtonBase, IconButton, Typography } from '@mui/material';
import { useTheme } from '@/styles/use-finance-theme';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';

export interface ShellSearchTriggerProps {
  onActivate: () => void;
}

export function ShellSearchTrigger({ onActivate }: ShellSearchTriggerProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { searchWidth } = theme.financeLayout.shell;
  const isMac = (() => {
    if (typeof navigator === 'undefined') return false;
    const uad = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData;
    if (uad?.platform) return /mac/i.test(uad.platform);
    return /mac|iphone|ipad/i.test(navigator.userAgent);
  })();
  const macKey = t('shell:search.macShortcut');
  const windowsKey = t('shell:search.windowsShortcut');
  const keyboardShortcut = isMac
    ? (macKey === 'shell:search.macShortcut' ? '⌘K' : macKey)
    : (windowsKey === 'shell:search.windowsShortcut' ? 'Ctrl+K' : windowsKey);

  const label = t('shell:search.label');

  return (
    <>
      <ButtonBase
        onClick={onActivate}
        aria-label={t('shell:search.ariaLabel')}
        aria-keyshortcuts="Meta+K Control+K"
        data-testid="search-trigger"
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          gap: 1,
          minWidth: searchWidth,
          maxWidth: searchWidth,
          minHeight: theme.financeLayout.shell.searchMinHeight,
          px: 1.25,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1.5,
          color: 'text.secondary',
          bgcolor: 'background.default',
          textAlign: 'left',
          transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'action.hover',
          },
          '&.Mui-focusVisible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        <SearchIcon fontSize="small" />
        <Typography variant="body2" noWrap sx={{ flex: 1 }}>
          {label}
        </Typography>
        <Box
          component="kbd"
          sx={{
            px: 0.75,
            py: 0.125,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
            fontFamily: 'inherit',
            fontSize: '0.7rem',
            lineHeight: 1.6,
            whiteSpace: 'nowrap',
          }}
        >
          {keyboardShortcut}
        </Box>
      </ButtonBase>
      <IconButton
        onClick={onActivate}
        aria-label={t('shell:search.ariaLabel')}
        data-testid="search-trigger-mobile"
        sx={{ display: { xs: 'inline-flex', md: 'none' } }}
      >
        <SearchIcon />
      </IconButton>
    </>
  );
}
